import React, { useEffect, useRef, useState, useCallback } from "react";
import { FileText, Minus, Plus, RotateCcw, Maximize2, Search, X } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import "./PDFViewer.css";

function fixFileNameEncoding(fileName) {
  if (!fileName) return fileName;
  
  try {
    
    if (!fileName.includes('Ã')) {
      return fileName;
    }
    
    
    try {
      
      const bytes = [];
      for (let i = 0; i < fileName.length; i++) {
        bytes.push(fileName.charCodeAt(i) & 0xFF);
      }
      const decoder = new TextDecoder('utf-8');
      const corrected = decoder.decode(new Uint8Array(bytes));
      if (!corrected.includes('Ã')) {
        return corrected;
      }
    } catch (e) {
    }
    
    return fileName
      .replace(/Ã©/g, 'é')
      .replace(/Ã¡/g, 'á')
      .replace(/Ã­/g, 'í')
      .replace(/Ã³/g, 'ó')
      .replace(/Ãº/g, 'ú')
      .replace(/Ã£/g, 'ã')
      .replace(/Ãµ/g, 'õ')
      .replace(/Ã§/g, 'ç')
      .replace(/Ãª/g, 'ê')
      .replace(/Ã´/g, 'ô')
      .replace(/Ã /g, 'à')
      .replace(/Ã‰/g, 'É')
      .replace(/Ã/g, 'à');
  } catch (err) {
    console.error('Erro ao corrigir encoding do nome:', err);
    return fileName;
  }
}

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDFViewer = ({ pdfData, currentPage, onPageChange }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [rendering, setRendering] = useState(false);
  const [autoFit, setAutoFit] = useState(true);
  const [pageInputValue, setPageInputValue] = useState(currentPage);
  const [canvasReady, setCanvasReady] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'https://pdf-processor-1jta.onrender.com';

  const pdfUrl = pdfData?.fileId
    ? `${API_URL}/uploads/${pdfData.fileId}.pdf`
    : null;

  const calculateAutoScale = useCallback(async (page) => {
    if (!page) {
      console.warn("Página não fornecida para calculateAutoScale");
      return 1.5;
    }

    if (!canvasContainerRef.current) {
      return 1.5;
    }

    try {
      const container = canvasContainerRef.current;
      const containerWidth = container.clientWidth - 30; 
      const containerHeight = container.clientHeight - 30;

      if (containerWidth <= 0 || containerHeight <= 0) {
        console.warn(`Dimensões do container inválidas: ${containerWidth}x${containerHeight}`);
        return 1.5;
      }

      const viewport = page.getViewport({ scale: 1.0 });
      const pageWidth = viewport.width;
      const pageHeight = viewport.height;

      if (pageWidth <= 0 || pageHeight <= 0) {
        console.warn(`Dimensões da página inválidas: ${pageWidth}x${pageHeight}`);
        return 1.5;
      }

      
      const scaleX = (containerWidth * 0.98) / pageWidth;
      const scaleY = (containerHeight * 0.98) / pageHeight;

      if (!isFinite(scaleX) || !isFinite(scaleY)) {
        console.warn(`Scale calculado inválido: scaleX=${scaleX}, scaleY=${scaleY}`);
        return 1.5;
      }

      
      const calculatedScale = Math.min(scaleX, scaleY, 3.5); 

      const finalScale = Math.max(calculatedScale, 0.5);
      
      if (!isFinite(finalScale) || finalScale <= 0) {
        console.warn(`Scale final inválido: ${finalScale}`);
        return 1.5;
      }

      return finalScale;
    } catch (err) {
      console.error("Erro ao calcular autoScale:", err);
      return 1.5;
    }
  }, []);

  useEffect(() => {
    if (!pdfUrl) {
      setPdf(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setPdf(null);
    setScale(1.5);
    isInitialLoadRef.current = true;

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          cMapUrl: `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/cmaps/`,
          cMapPacked: true,
        });
        const pdfLoaded = await loadingTask.promise;

        let attempts = 0;
        while (
          attempts < 10 &&
          (!canvasContainerRef.current ||
            canvasContainerRef.current.clientWidth <= 0 ||
            canvasContainerRef.current.clientHeight <= 0)
        ) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts++;
        }

        setPdf(pdfLoaded);
        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
        setError(`Erro ao carregar o PDF: ${err.message || 'Tente novamente.'}`);
        setLoading(false);
        setPdf(null);
      }
    };

    loadPdf();
  }, [pdfUrl]);

  
  useEffect(() => {
    if (!autoFit || !pdf || !currentPage) return;

    const handleResize = async () => {
      if (rendering) return; 

      try {
        await new Promise((resolve) => setTimeout(resolve, 100)); 
        const page = await pdf.getPage(currentPage);
        const newScale = await calculateAutoScale(page);
        if (newScale) {
          setScale(newScale);
        }
      } catch (err) {
        console.error("Erro ao calcular scale:", err);
      }
    };

    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 300);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(resizeTimeout);
    };
  }, [pdf, currentPage, autoFit, rendering, calculateAutoScale]);

  useEffect(() => {
    if (!autoFit || !pdf || !currentPage || rendering || loading) return;

    const adjustScaleForPage = async () => {
      try {
        let attempts = 0;
        while (attempts < 5 && (!canvasContainerRef.current || 
               canvasContainerRef.current.clientWidth <= 0 || 
               canvasContainerRef.current.clientHeight <= 0)) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          attempts++;
        }

        if (!canvasContainerRef.current) {
          console.warn("Container não disponível para ajuste de scale");
          return;
        }

        const page = await pdf.getPage(currentPage);
        if (!page) {
          console.warn(`Página ${currentPage} não encontrada`);
          return;
        }

        const newScale = await calculateAutoScale(page);
        if (newScale && newScale > 0 && isFinite(newScale)) {
          setScale(prevScale => {
            if (Math.abs(prevScale - newScale) > 0.1) {
              return newScale;
            }
            return prevScale;
          });
        }
      } catch (err) {
        console.error("Erro ao ajustar scale para página:", err);
      }
    };

    adjustScaleForPage();
  }, [pdf, currentPage, autoFit, calculateAutoScale, rendering, loading]);

  useEffect(() => {
    if (!pdf || !currentPage || loading || !canvasReady) return;

    if (currentPage < 1 || currentPage > pdf.numPages) {
      console.error(`Página inválida: ${currentPage} (total: ${pdf.numPages})`);
      return;
    }

    if (!scale || scale <= 0 || scale > 10) {
      console.error(`Scale inválido: ${scale}`);
      setScale(1.5);
      return;
    }

    const renderPage = async () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
        }
        renderTaskRef.current = null;
      }

      setRendering(true);
      setError(null);
      
      try {
        const page = await pdf.getPage(currentPage);
        
        if (!page) {
          throw new Error(`Página ${currentPage} não encontrada`);
        }

        const viewport = page.getViewport({ scale: Math.max(0.1, Math.min(scale, 10)) });

        const canvas = canvasRef.current;
        if (!canvas) {
          setRendering(false);
          return;
        }

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Não foi possível obter contexto 2D do canvas");
        }

      
        const width = Math.floor(viewport.width);
        const height = Math.floor(viewport.height);

        if (width <= 0 || height <= 0) {
          throw new Error(`Dimensões inválidas do canvas: ${width}x${height}`);
        }

        canvas.width = width;
        canvas.height = height;

        
        context.clearRect(0, 0, canvas.width, canvas.height);

       
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

       
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        
      
        renderTaskRef.current = null;
        setRendering(false);
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      } catch (err) {
        
        if (err.name === 'RenderingCancelledException' || err.message?.includes('cancel')) {
          
          return;
        }
        console.error("Erro ao renderizar página:", err);
        setError(`Erro ao renderizar a página: ${err.message || 'Erro desconhecido'}`);
        renderTaskRef.current = null;
        setRendering(false);
      }
    };

    renderPage();
    
    
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          
        }
        renderTaskRef.current = null;
      }
    };
  }, [pdf, currentPage, scale, loading, canvasReady]);

  useEffect(() => {
    let rafId;
    const ensureCanvas = () => {
      if (canvasRef.current) {
        setCanvasReady(true);
        return;
      }
      rafId = requestAnimationFrame(ensureCanvas);
    };

    setCanvasReady(false);
    ensureCanvas();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [pdf, currentPage]);

  
  useEffect(() => {
    if (pdf && currentPage > pdf.numPages) {
      onPageChange(1);
    }
    
    setPageInputValue(currentPage);
  }, [pdf, currentPage, onPageChange]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pdf && currentPage < pdf.numPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    
    if (autoFit) {
      setAutoFit(false);
    }
    setScale((prev) => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    
    if (autoFit) {
      setAutoFit(false);
    }
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleZoomReset = async () => {
    if (autoFit && pdf && currentPage) {
      
      const page = await pdf.getPage(currentPage);
      const newScale = await calculateAutoScale(page);
      setScale(newScale);
    } else {
      
      setScale(1.5);
    }
  };

  const handleAutoFitToggle = async () => {
    if (!pdf || !currentPage) return;

    const newAutoFit = !autoFit;
    setAutoFit(newAutoFit);

    if (newAutoFit) {
      
      const page = await pdf.getPage(currentPage);
      const newScale = await calculateAutoScale(page);
      setScale(newScale);
    }
  };

  if (loading) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-loading">
          <div className="spinner"></div>
          <p>Carregando PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-error">
          <p>
            <X size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!pdf) {
    return (
      <div className="pdf-viewer">
        <div className="pdf-error">
          <p>Nenhum PDF carregado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer" ref={containerRef}>
      <div className="pdf-header">
        <div className="pdf-title">
          <h3>
            <FileText size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {pdfData?.fileName ? fixFileNameEncoding(pdfData.fileName) : "PDF"}
          </h3>
        </div>
        <div className="pdf-info">
          <span className="page-indicator">
            Página {currentPage} de {pdf.numPages}
          </span>
        </div>
      </div>

      <div className="pdf-canvas-container" ref={canvasContainerRef}>
        {rendering && isInitialLoadRef.current ? (
          <div className="pdf-rendering-overlay">
            <div className="spinner"></div>
          </div>
        ) : null}
        <canvas
          ref={canvasRef}
          className="pdf-canvas"
          style={{
            width: "auto",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />
      </div>

      <div className="pdf-controls">
        <button
          className="pdf-control-btn"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          title="Página anterior"
        >
          ← Anterior
        </button>

        <div className="pdf-page-input-group">
          <input
            type="number"
            min="1"
            max={pdf.numPages}
            value={pageInputValue}
            onChange={(e) => {
              const value = e.target.value;
              setPageInputValue(value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                  const page = parseInt(pageInputValue);
                if (!isNaN(page) && page >= 1 && page <= pdf.numPages) {
                  onPageChange(page);
                  e.target.blur();
                } else {
                  setPageInputValue(currentPage);
                }
              } else if (e.key === 'Escape') {
                setPageInputValue(currentPage);
                e.target.blur();
              }
            }}
            onBlur={() => {
              const page = parseInt(pageInputValue);
              if (isNaN(page) || page < 1 || page > pdf.numPages) {
                setPageInputValue(currentPage);
              } else if (page !== currentPage) {
                onPageChange(page);
              }
            }}
            className="pdf-page-input"
            placeholder={`1-${pdf.numPages}`}
            title={`Digite o número da página (1 a ${pdf.numPages}) e pressione Enter`}
          />
          <span className="pdf-page-total">/ {pdf.numPages}</span>
        </div>

        <button
          className="pdf-control-btn"
          onClick={handleNextPage}
          disabled={currentPage === pdf.numPages}
          title="Próxima página"
        >
          Próxima →
        </button>
      </div>

      <div className="pdf-zoom-controls">
        <button
          className="pdf-zoom-btn"
          onClick={handleZoomOut}
          disabled={scale <= 0.5}
          title="Diminuir zoom"
        >
          <Minus size={18} />
        </button>
        <span className="pdf-zoom-indicator">
          {Math.round(scale * 100)}%
        </span>
        <button
          className={`pdf-zoom-btn ${autoFit ? 'active' : ''}`}
          onClick={handleAutoFitToggle}
          title={autoFit ? "Ajuste automático ativado" : "Ativar ajuste automático"}
        >
          {autoFit ? <Maximize2 size={18} /> : <Search size={18} />}
        </button>
        <button
          className="pdf-zoom-btn"
          onClick={handleZoomReset}
          title="Resetar zoom"
        >
          <RotateCcw size={18} />
        </button>
        <button
          className="pdf-zoom-btn"
          onClick={handleZoomIn}
          disabled={scale >= 3.0}
          title="Aumentar zoom"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};

export default PDFViewer;
